import { LitElement, html, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { BoardItem, BoardComment, CreateCommentRequest } from '../../types/board.js';
import type { AgentSummary } from '../../types/agent.js';
import type { Project } from '../../types/project.js';
import * as boardApi from '../../services/board-api.js';
import * as executionApi from '../../services/execution-api.js';
import * as projectApi from '../../services/project-api.js';
import { wsClient } from '../../services/websocket-client.js';
import { PRIORITY_LABELS, SOURCE_TYPE_LABELS, ASSIGNEE_LABELS, isAgentAssignee } from './board-constants.js';
import type { SkillOption } from '../../services/board-api.js';
import { getCaretCoordinates, extractMentionContext } from '../../utils/textarea-caret.js';
import type { CpFileMentionDropdown } from '../shared/cp-file-mention-dropdown.js';

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-6', label: 'Sonnet' },
  { value: 'claude-opus-4-6', label: 'Opus' },
  { value: 'claude-haiku-4-5-20251001', label: 'Haiku' },
] as const;

const LOG_RING_BUFFER_MAX = 500;

@customElement('cp-board-detail')
export class CpBoardDetail extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) item: BoardItem | null = null;

  @state() private _comments: BoardComment[] = [];
  @state() private _loading = false;
  @state() private _submitting = false;
  @state() private _commentText = '';
  @state() private _skills: SkillOption[] = [];
  @state() private _savingAssignee = false;
  @state() private _savingAgent = false;
  @state() private _executionLog: string[] = [];
  @state() private _selectedModel = 'claude-sonnet-4-6';
  @state() private _savingModel = false;
  @state() private _confirmingDelete = false;
  @state() private _deleting = false;
  @state() private _projects: Project[] = [];
  @state() private _savingProject = false;
  @state() private _mentionOpen = false;
  @state() private _mentionQuery = '';
  @state() private _mentionAnchor = { top: 0, left: 0 };

  @query('.cp-board-detail__comment-input') private _textarea!: HTMLTextAreaElement;
  @query('.cp-board-detail__log-entries') private _logContainer?: HTMLElement;

  private _boundOnKeydown = this._onKeydown.bind(this);
  private _boundOnWsMessage = this._onWsMessage.bind(this);

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._boundOnKeydown);
    wsClient.addEventListener('ws-message', this._boundOnWsMessage);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._boundOnKeydown);
    wsClient.removeEventListener('ws-message', this._boundOnWsMessage);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('item') && this.item) {
      void this._loadComments();
      if (this.item.sourceType === 'goal' && this._skills.length === 0) {
        void this._loadSkills();
      }
      if (this.item.sourceType === 'todo' && this._projects.length === 0) {
        void this._loadProjects();
      }
      // Reset state when item changes
      this._confirmingDelete = false;
      this._executionLog = [];
      this._selectedModel = this.item.executionModel ?? 'claude-sonnet-4-6';
      void this._loadSavedModel();
    }

    // Auto-scroll log panel to bottom on new entries
    if (changed.has('_executionLog') && this._logContainer) {
      this._logContainer.scrollTop = this._logContainer.scrollHeight;
    }
  }

  private _onWsMessage(e: Event): void {
    const detail = (e as CustomEvent).detail as { type: string; payload?: Record<string, unknown> };
    if (!this.item) return;

    const [sourceType, , sourceId] = this.item.id.split(':');

    if (
      (detail.type === 'comment:created' || detail.type === 'comment:deleted') &&
      detail.payload?.['source_type'] === sourceType &&
      detail.payload?.['source_id'] === sourceId
    ) {
      void this._loadComments();
    }

    if (detail.type === 'execution:log' && detail.payload?.['ticketId'] === this.item.id) {
      const event = detail.payload?.['event'] as Record<string, unknown> | undefined;
      if (event) {
        const line = this._formatLogEvent(event);
        if (line) {
          this._executionLog = [...this._executionLog, line].slice(-LOG_RING_BUFFER_MAX);
        }
      }
    }

    // Clear log when a new execution starts for this ticket
    if (detail.type === 'execution:started' && detail.payload?.['ticketId'] === this.item.id) {
      this._executionLog = [];
    }
  }

  private _formatLogEvent(event: Record<string, unknown>): string | null {
    if (event['type'] === 'text' && typeof event['content'] === 'string') {
      return event['content'];
    }
    if (event['type'] === 'assistant') {
      const msg = event['message'] as { content?: Array<{ type: string; text?: string }> } | undefined;
      const texts = msg?.content?.filter(c => c.type === 'text').map(c => c.text ?? '').filter(Boolean);
      if (texts?.length) return texts.join('\n');
    }
    if (event['type'] === 'result') {
      const subtype = event['subtype'] as string | undefined;
      return subtype ? `[${subtype}]` : '[result]';
    }
    if (event['type'] === 'tool_use') {
      const name = event['name'] as string | undefined;
      return `[Tool: ${name ?? 'unknown'}]`;
    }
    // Skip events that don't have user-readable content
    return null;
  }

  private async _loadSavedModel(): Promise<void> {
    if (!this.item) return;
    // Only load from API if no live execution model is available
    if (this.item.executionModel) {
      this._selectedModel = this.item.executionModel;
      return;
    }
    try {
      const model = await executionApi.getItemModel(this.item.id);
      this._selectedModel = model;
    } catch {
      // Keep default
    }
  }

  private async _onModelChange(e: Event): Promise<void> {
    if (!this.item) return;
    const model = (e.target as HTMLSelectElement).value;
    this._selectedModel = model;
    this._savingModel = true;
    try {
      await executionApi.setModel(this.item.id, model);
    } catch {
      // Revert on error
      this._selectedModel = this.item.executionModel ?? 'claude-sonnet-4-6';
    }
    this._savingModel = false;
  }

  private async _loadComments(): Promise<void> {
    if (!this.item) return;
    this._loading = true;

    const [sourceType, projectIdStr, sourceId] = this.item.id.split(':');
    const projectId = sourceType === 'backlog' ? projectIdStr : undefined;

    try {
      this._comments = await boardApi.getComments(sourceType, sourceId, projectId);
    } catch {
      this._comments = [];
    }

    this._loading = false;
  }

  private async _loadSkills(): Promise<void> {
    try {
      this._skills = await boardApi.getSkills();
    } catch {
      this._skills = [];
    }
  }

  private async _onAssigneeChange(e: Event): Promise<void> {
    if (!this.item || this.item.sourceType !== 'goal') return;
    const newAssignee = (e.target as HTMLSelectElement).value;

    const [, , sourceId] = this.item.id.split(':');
    const goalId = Number(sourceId);

    this._savingAssignee = true;
    try {
      await boardApi.updateGoalAssignee(goalId, newAssignee);
      this.item = { ...this.item, assignee: newAssignee };
      this.dispatchEvent(new CustomEvent('board-detail-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch {
      // Revert select to previous value on error
    }
    this._savingAssignee = false;
  }

  private async _onAgentSelected(e: CustomEvent<{ agentId: number; agent: AgentSummary }>): Promise<void> {
    if (!this.item || this.item.sourceType !== 'goal') return;

    const { agentId, agent } = e.detail;
    const [, , sourceId] = this.item.id.split(':');
    const goalId = Number(sourceId);

    this._savingAgent = true;
    try {
      await boardApi.updateGoalAgentId(goalId, agentId);
      this.item = {
        ...this.item,
        agentId,
        agent: { id: agent.id, name: agent.name, color: agent.color, has_avatar: agent.has_avatar },
      };
      this.dispatchEvent(new CustomEvent('board-detail-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.warn('Failed to update agent override:', err);
    }
    this._savingAgent = false;
  }

  private async _removeAgentOverride(): Promise<void> {
    if (!this.item || this.item.sourceType !== 'goal') return;

    const [, , sourceId] = this.item.id.split(':');
    const goalId = Number(sourceId);

    this._savingAgent = true;
    try {
      await boardApi.updateGoalAgentId(goalId, null);
      this.item = { ...this.item, agentId: null, agent: undefined };
      this.dispatchEvent(new CustomEvent('board-detail-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.warn('Failed to remove agent override:', err);
    }
    this._savingAgent = false;
  }

  private async _loadProjects(): Promise<void> {
    try {
      this._projects = await projectApi.getProjects('active');
    } catch {
      this._projects = [];
    }
  }

  private async _onProjectChange(e: Event): Promise<void> {
    if (!this.item || this.item.sourceType !== 'todo') return;
    const value = (e.target as HTMLSelectElement).value;
    const linkedProjectId = value ? Number(value) : null;

    const [, , sourceId] = this.item.id.split(':');
    const todoId = Number(sourceId);

    this._savingProject = true;
    try {
      await boardApi.updateTodoLinkedProject(todoId, linkedProjectId);
      const project = this._projects.find(p => p.id === linkedProjectId);
      this.item = { ...this.item, sourceProjectId: linkedProjectId ?? 0, sourceProject: project?.name ?? this.item.sourceProject };
      this.dispatchEvent(new CustomEvent('board-detail-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch {
      // Revert on error
    }
    this._savingProject = false;
  }

  private async _deleteComment(comment: BoardComment): Promise<void> {
    if (!this.item) return;

    const [sourceType, projectIdStr, sourceId] = this.item.id.split(':');
    const projectId = sourceType === 'backlog' ? projectIdStr : undefined;

    try {
      await boardApi.deleteComment(sourceType, sourceId, comment.id, projectId);
      this._comments = this._comments.filter(c => c.id !== comment.id);
    } catch {
      // Silently fail — WS event will sync if needed
    }
  }

  private async _submitComment(): Promise<void> {
    if (!this.item || !this._commentText.trim()) return;
    this._submitting = true;

    const [sourceType, projectIdStr, sourceId] = this.item.id.split(':');

    const data: CreateCommentRequest = {
      source_type: sourceType,
      source_id: sourceId,
      content: this._commentText.trim(),
      author: 'user',
      ...(sourceType === 'backlog' && projectIdStr ? { project_id: Number(projectIdStr) } : {}),
    };

    try {
      const comment = await boardApi.createComment(data);
      this._comments = [...this._comments, comment];
      this._commentText = '';
      if (this._textarea) this._textarea.value = '';
    } catch {
      // Keep text so user can retry
    }

    this._submitting = false;
  }

  private get _canDelete(): boolean {
    if (!this.item) return false;
    return this.item.sourceType === 'todo' || this.item.sourceType === 'goal' || this.item.sourceType === 'backlog';
  }

  private _requestDelete(): void {
    this._confirmingDelete = true;
  }

  private _cancelDelete(): void {
    this._confirmingDelete = false;
  }

  private async _confirmDelete(): Promise<void> {
    if (!this.item) return;
    this._deleting = true;
    try {
      await boardApi.deleteBoardItem(this.item.id);
      this._confirmingDelete = false;
      this.dispatchEvent(new CustomEvent('board-detail-deleted', {
        bubbles: true,
        composed: true,
        detail: { itemId: this.item.id },
      }));
      this._close();
    } catch {
      // Keep dialog open on error
    }
    this._deleting = false;
  }

  private _onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      if (this._confirmingDelete) {
        this._cancelDelete();
        return;
      }
      this._close();
    }
  }

  private _onOverlayClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).classList.contains('cp-board-detail__overlay')) {
      this._close();
    }
  }

  private _close(): void {
    this.dispatchEvent(new CustomEvent('board-detail-close', {
      bubbles: true,
      composed: true,
    }));
  }

  private _onInput(e: InputEvent): void {
    const textarea = e.target as HTMLTextAreaElement;
    this._commentText = textarea.value;
    this._updateMention(textarea);
  }

  private _updateMention(textarea: HTMLTextAreaElement): void {
    const projectId = this.item?.sourceProjectId ?? 0;
    if (!projectId) {
      this._mentionOpen = false;
      return;
    }
    const ctx = extractMentionContext(textarea);
    if (ctx) {
      this._mentionQuery = ctx.query;
      this._mentionAnchor = getCaretCoordinates(textarea);
      this._mentionOpen = true;
    } else {
      this._mentionOpen = false;
    }
  }

  private _onCommentKeydown(e: KeyboardEvent): void {
    if (this._mentionOpen) {
      const dropdown = this.querySelector('cp-file-mention-dropdown') as CpFileMentionDropdown | null;
      if (dropdown?.handleKeydown(e)) return;
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void this._submitComment();
    }
  }

  private _onFileSelected(e: CustomEvent<{ path: string }>): void {
    if (!this._textarea) return;
    const ctx = extractMentionContext(this._textarea);
    if (!ctx) return;

    const before = this._commentText.substring(0, ctx.startIndex);
    const after = this._commentText.substring(this._textarea.selectionEnd);
    this._commentText = `${before}@${e.detail.path} ${after}`;
    this._mentionOpen = false;

    this.updateComplete.then(() => {
      if (this._textarea) {
        const newPos = ctx.startIndex + e.detail.path.length + 2;
        this._textarea.value = this._commentText;
        this._textarea.setSelectionRange(newPos, newPos);
        this._textarea.focus();
      }
    });
  }

  private _onMentionDismissed(): void {
    this._mentionOpen = false;
  }

  private _formatDate(dateStr: string): string {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  }

  private _renderMarkdown(text: string): ReturnType<typeof html> {
    const rawHtml = marked.parse(text, { async: false }) as string;
    const sanitized = DOMPurify.sanitize(rawHtml);
    return html`${unsafeHTML(sanitized)}`;
  }

  private _renderAssigneeDropdown() {
    if (!this.item || this.item.sourceType !== 'goal') return nothing;

    const currentAssignee = this.item.assignee ?? 'michael';
    const isAgent = isAgentAssignee(currentAssignee);

    return html`
      <div class="cp-board-detail__meta-row">
        <span class="cp-board-detail__meta-label">Zugewiesen an</span>
        <div class="cp-board-detail__assignee-control">
          ${isAgent ? html`<span class="cp-board-detail__assignee-badge">Agent</span>` : nothing}
          <select
            class="cp-board-detail__assignee-select"
            .value=${currentAssignee}
            @change=${this._onAssigneeChange}
            ?disabled=${this._savingAssignee}
          >
            <optgroup label="Personen">
              ${Object.entries(ASSIGNEE_LABELS).map(([value, label]) =>
                html`<option value=${value} ?selected=${currentAssignee === value}>${label}</option>`
              )}
            </optgroup>
            ${this._skills.length > 0 ? html`
              <optgroup label="Skills">
                ${this._skills.map(skill =>
                  html`<option value=${skill.name} ?selected=${currentAssignee === skill.name}>${skill.name}</option>`
                )}
              </optgroup>
            ` : nothing}
          </select>
        </div>
      </div>
    `;
  }

  private _renderAgentOverride() {
    if (!this.item || this.item.sourceType !== 'goal') return nothing;

    const currentAgentId = this.item.agentId ?? null;

    return html`
      <div class="cp-board-detail__meta-row">
        <span class="cp-board-detail__meta-label">Agent-Override</span>
        <div class="cp-board-detail__agent-control">
          <cp-agent-selector
            .value=${currentAgentId}
            ?disabled=${this._savingAgent}
            @agent-selected=${this._onAgentSelected}
          ></cp-agent-selector>
          ${currentAgentId != null ? html`
            <button
              class="cp-board-detail__agent-remove"
              @click=${() => void this._removeAgentOverride()}
              ?disabled=${this._savingAgent}
              title="Agent-Override entfernen"
            >&times;</button>
          ` : nothing}
        </div>
      </div>
    `;
  }

  private _renderProgress() {
    const { progress } = this.item!;
    if (!progress) return nothing;

    const percent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

    return html`
      <div class="cp-board-detail__progress">
        <div class="cp-board-detail__progress-header">
          <span class="cp-board-detail__progress-label">Story-Fortschritt</span>
          <span class="cp-board-detail__progress-text">${progress.done}/${progress.total} Stories</span>
        </div>
        <div class="cp-board-detail__progress-bar">
          <div class="cp-board-detail__progress-fill" style="width: ${percent}%"></div>
        </div>
      </div>
    `;
  }

  private _renderDescription() {
    const item = this.item!;
    if (!item.description) return nothing;

    return html`
      <div class="cp-board-detail__goal-details">
        <div class="cp-board-detail__goal-description">
          <span class="cp-board-detail__meta-label">Beschreibung</span>
          <div class="cp-board-detail__goal-description-text">${this._renderMarkdown(item.description)}</div>
        </div>
      </div>
    `;
  }

  private _renderGoalDetails() {
    const item = this.item!;
    if (!item.sourceGoal) return nothing;

    return html`
      <div class="cp-board-detail__goal-details">
        <div class="cp-board-detail__meta-row">
          <span class="cp-board-detail__meta-label">Quelle</span>
          <span class="cp-board-detail__meta-value">${item.sourceGoal}</span>
        </div>
      </div>
    `;
  }

  private _renderModelSelector() {
    return html`
      <div class="cp-board-detail__meta-row">
        <span class="cp-board-detail__meta-label">Model</span>
        <select
          class="cp-board-detail__model-select"
          .value=${this._selectedModel}
          @change=${this._onModelChange}
          ?disabled=${this._savingModel}
        >
          ${MODEL_OPTIONS.map(opt => html`
            <option value=${opt.value} ?selected=${this._selectedModel === opt.value}>${opt.label}</option>
          `)}
        </select>
      </div>
    `;
  }

  private _renderProjectSelector() {
    if (!this.item || this.item.sourceType !== 'todo') return nothing;

    const currentProjectId = this.item.sourceProjectId || '';

    return html`
      <div class="cp-board-detail__meta-row">
        <span class="cp-board-detail__meta-label">Specwright-Projekt</span>
        <select
          class="cp-board-detail__model-select"
          .value=${String(currentProjectId)}
          @change=${this._onProjectChange}
          ?disabled=${this._savingProject}
        >
          <option value="">-- Kein Projekt --</option>
          ${this._projects.map(p => html`
            <option value=${p.id} ?selected=${p.id === currentProjectId}>${p.name}</option>
          `)}
        </select>
      </div>
    `;
  }

  private _renderExecutionLog() {
    if (this.item?.executionStatus !== 'running') return nothing;

    return html`
      <div class="cp-board-detail__execution-log">
        <div class="cp-board-detail__execution-log-header">
          <span class="cp-board-detail__execution-log-title">Live-Log</span>
          <span class="cp-board-detail__execution-log-indicator"></span>
        </div>
        <div class="cp-board-detail__log-entries">
          ${this._executionLog.length === 0
            ? html`<div class="cp-board-detail__log-empty">Warte auf Ausgabe...</div>`
            : this._executionLog.map(line => html`<div class="cp-board-detail__log-entry">${line}</div>`)
          }
        </div>
      </div>
    `;
  }

  private _renderComment(comment: BoardComment) {
    const isAgent = comment.author === 'agent';

    return html`
      <div class="cp-board-detail__comment ${isAgent ? 'cp-board-detail__comment--agent' : ''}">
        <div class="cp-board-detail__comment-header">
          <span class="cp-board-detail__comment-author ${isAgent ? 'cp-board-detail__comment-author--agent' : ''}">
            ${isAgent ? 'Agent' : 'User'}
          </span>
          <div class="cp-board-detail__comment-header-right">
            <span class="cp-board-detail__comment-time">${this._formatDate(comment.created_at)}</span>
            <button
              class="cp-board-detail__comment-delete"
              @click=${() => void this._deleteComment(comment)}
              title="Kommentar loeschen"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </button>
          </div>
        </div>
        <div class="cp-board-detail__comment-content">${this._renderMarkdown(comment.content)}</div>
      </div>
    `;
  }

  private _renderComments() {
    if (this._loading) {
      return html`<div class="cp-board-detail__comments-loading">Lade Kommentare...</div>`;
    }

    if (this._comments.length === 0) {
      return html`<div class="cp-board-detail__comments-empty">Noch keine Kommentare</div>`;
    }

    return html`
      <div class="cp-board-detail__comments-list">
        ${this._comments.map(c => this._renderComment(c))}
      </div>
    `;
  }

  render() {
    if (!this.item) return nothing;

    const item = this.item;

    return html`
      <div class="cp-board-detail__overlay" @click=${this._onOverlayClick}>
        <div class="cp-board-detail__panel">
          <div class="cp-board-detail__header">
            <h2 class="cp-board-detail__title">${item.title}</h2>
            <div class="cp-board-detail__header-actions">
              ${this._canDelete ? html`
                <button
                  class="cp-board-detail__delete-btn"
                  @click=${this._requestDelete}
                  ?disabled=${this._deleting}
                  title="Ticket loeschen"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                </button>
              ` : nothing}
              <button class="cp-board-detail__close" @click=${this._close} aria-label="Schliessen">&times;</button>
            </div>
          </div>

          ${this._confirmingDelete ? html`
            <div class="cp-board-detail__confirm-delete">
              <p class="cp-board-detail__confirm-text">Ticket "${item.title}" wirklich loeschen?</p>
              <div class="cp-board-detail__confirm-actions">
                <button
                  class="cp-board-detail__confirm-cancel"
                  @click=${this._cancelDelete}
                  ?disabled=${this._deleting}
                >Abbrechen</button>
                <button
                  class="cp-board-detail__confirm-yes"
                  @click=${() => void this._confirmDelete()}
                  ?disabled=${this._deleting}
                >${this._deleting ? 'Loesche...' : 'Loeschen'}</button>
              </div>
            </div>
          ` : nothing}

          <div class="cp-board-detail__content">
            <div class="cp-board-detail__meta">
              ${item.sourceType === 'todo' ? this._renderProjectSelector() : html`
                <div class="cp-board-detail__meta-row">
                  <span class="cp-board-detail__meta-label">Projekt</span>
                  <span class="cp-board-detail__meta-value cp-board-detail__project-badge">${item.sourceProject}</span>
                </div>
              `}
              <div class="cp-board-detail__meta-row">
                <span class="cp-board-detail__meta-label">Typ</span>
                <span class="cp-board-detail__meta-value">${SOURCE_TYPE_LABELS[item.sourceType] ?? item.sourceType}</span>
              </div>
              <div class="cp-board-detail__meta-row">
                <span class="cp-board-detail__meta-label">Status</span>
                <span class="cp-board-detail__meta-value">${item.boardColumn.replace('_', ' ')}</span>
              </div>
              <div class="cp-board-detail__meta-row">
                <span class="cp-board-detail__meta-label">Prioritaet</span>
                <span class="cp-board-detail__meta-value cp-board-detail__priority cp-board-detail__priority--${item.priority}">
                  ${PRIORITY_LABELS[item.priority] ?? item.priority}
                </span>
              </div>
              ${this._renderAssigneeDropdown()}
              ${this._renderAgentOverride()}
              ${this._renderModelSelector()}
            </div>

            ${this._renderExecutionLog()}
            ${item.sourceType === 'spec' ? this._renderProgress() : nothing}
            ${item.sourceType === 'goal' ? this._renderGoalDetails() : nothing}
            ${this._renderDescription()}

            <div class="cp-board-detail__comments-section">
              <h3 class="cp-board-detail__section-title">Kommentare</h3>
              ${this._renderComments()}

              <div class="cp-board-detail__comment-form">
                <textarea
                  class="cp-board-detail__comment-input"
                  placeholder="Kommentar schreiben... (Cmd+Enter zum Absenden)"
                  .value=${this._commentText}
                  @input=${this._onInput}
                  @keydown=${this._onCommentKeydown}
                  ?disabled=${this._submitting}
                ></textarea>
                <cp-file-mention-dropdown
                  .projectId=${this.item?.sourceProjectId ?? 0}
                  .open=${this._mentionOpen}
                  .query=${this._mentionQuery}
                  .anchorRect=${this._mentionAnchor}
                  @file-selected=${this._onFileSelected}
                  @mention-dismissed=${this._onMentionDismissed}
                ></cp-file-mention-dropdown>
                <button
                  class="cp-board-detail__comment-submit"
                  @click=${() => void this._submitComment()}
                  ?disabled=${this._submitting || !this._commentText.trim()}
                >
                  ${this._submitting ? 'Sende...' : 'Senden'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-board-detail': CpBoardDetail;
  }
}
